import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { CalendarCheck, Check, Phone, WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import { FollowupDateTime, emptyDateTime, toIso, formatFollowup } from "@/components/FollowupDateTime";

export default function Followups() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [leads, setLeads] = useState([]);
  const [form, setForm] = useState({ lead_id: "", notes: "" });
  const [when, setWhen] = useState(emptyDateTime);

  const load = async () => {
    const r = await api.get("/followups");
    setItems(r.data);
  };
  useEffect(() => { load(); api.get("/leads").then(r => setLeads(r.data)); }, []);

  const add = async (e) => {
    e.preventDefault();
    const scheduled_at = toIso(when);
    if (!scheduled_at) { toast.error("Please select a follow-up date"); return; }
    try {
      await api.post("/followups", { ...form, scheduled_at });
      setForm({ lead_id: "", notes: "" });
      setWhen(emptyDateTime);
      toast.success("Follow-up saved");
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save follow-up"); }
  };

  const complete = async (id) => {
    await api.patch(`/followups/${id}`, { completed: true });
    toast.success("Follow-up completed");
    await load();
  };

  const bucketize = () => {
    const now = new Date();
    const today = now.toDateString();
    let list = items;
    if (filter === "overdue") list = items.filter(i => !i.completed && new Date(i.scheduled_at) < now && new Date(i.scheduled_at).toDateString() !== today);
    else if (filter === "today") list = items.filter(i => !i.completed && new Date(i.scheduled_at).toDateString() === today);
    else if (filter === "upcoming") list = items.filter(i => !i.completed && new Date(i.scheduled_at) > now && new Date(i.scheduled_at).toDateString() !== today);
    else if (filter === "done") list = items.filter(i => i.completed);
    return list;
  };

  const list = bucketize();
  const counts = {
    all: items.length,
    overdue: items.filter(i => !i.completed && new Date(i.scheduled_at) < new Date() && new Date(i.scheduled_at).toDateString() !== new Date().toDateString()).length,
    today: items.filter(i => !i.completed && new Date(i.scheduled_at).toDateString() === new Date().toDateString()).length,
    upcoming: items.filter(i => !i.completed && new Date(i.scheduled_at) > new Date()).length,
    done: items.filter(i => i.completed).length,
  };

  const tabs = [
    ["all", "All", "text-slate-300"],
    ["overdue", "Overdue", "text-rose-400"],
    ["today", "Today", "text-amber-400"],
    ["upcoming", "Upcoming", "text-sky-400"],
    ["done", "Completed", "text-emerald-400"],
  ];

  return (
    <div className="space-y-6" data-testid="followups-page">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Follow-ups</div>
        <h1 className="font-serif text-3xl md:text-4xl text-white mt-1">Reach the right lead, right on time</h1>
      </div>

      <form onSubmit={add} className="card-luxury p-4 flex flex-wrap gap-3" data-testid="fu-add-form">
        <select required value={form.lead_id} onChange={e => setForm({ ...form, lead_id: e.target.value })} data-testid="fu-add-lead"
          className="bg-[#060B14] border border-[#1E293B] focus:border-[#D4AF37] outline-none rounded-md px-3 py-2 text-white text-sm min-w-[200px]">
          <option value="">— Select lead —</option>
          {leads.map(l => <option key={l.id} value={l.id}>{l.name} • {l.mobile}</option>)}
        </select>
        <FollowupDateTime value={when} onChange={setWhen} />
        <input placeholder="Notes…" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="fu-add-notes"
          className="flex-1 min-w-[180px] bg-[#060B14] border border-[#1E293B] focus:border-[#D4AF37] outline-none rounded-md px-3 py-2 text-white text-sm" />
        <button data-testid="fu-add-btn" className="px-4 py-2 rounded-md bg-[#D4AF37] text-[#060B14] font-semibold text-sm hover:bg-[#F2CE6E]">Add Follow-up</button>
      </form>

      <div className="flex flex-wrap gap-2">
        {tabs.map(([k, label, color]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`fu-tab-${k}`}
            className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest border transition-colors ${filter === k ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : `border-[#1E293B] ${color} hover:border-[#D4AF37]/40`}`}>
            {label} <span className="ml-1 opacity-70">({counts[k]})</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.length === 0 && <div className="col-span-full text-slate-500 text-sm text-center py-10">No follow-ups in this bucket.</div>}
        {list.map(f => {
          const dt = new Date(f.scheduled_at);
          const today = new Date().toDateString() === dt.toDateString();
          const overdue = !f.completed && dt < new Date() && !today;
          const border = f.completed ? "border-emerald-400/30" : overdue ? "border-rose-400/30" : today ? "border-amber-400/30" : "border-[#1E293B]";
          const dot = f.completed ? "bg-emerald-400" : overdue ? "bg-rose-400" : today ? "bg-amber-400" : "bg-sky-400";
          const wa = (f.lead?.mobile || "").replace(/[^0-9]/g, "");
          return (
            <div key={f.id} className={`card-luxury p-4 border ${border}`} data-testid="fu-card">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${dot}`} />
                  <CalendarCheck className="text-[#D4AF37]" size={16} weight="duotone" />
                  <div className="text-xs text-slate-300" data-testid="fu-card-datetime">{formatFollowup(f.scheduled_at)}</div>
                </div>
                {!f.completed && (
                  <button onClick={() => complete(f.id)} title="Complete" data-testid={`fu-complete-${f.id}`}
                    className="w-7 h-7 rounded-full border border-emerald-400/30 text-emerald-400 flex items-center justify-center hover:bg-emerald-400/10">
                    <Check size={14} weight="bold" />
                  </button>
                )}
              </div>
              <Link to={`/leads/${f.lead_id}`} className="mt-3 block">
                <div className="font-serif text-lg text-white hover:text-[#D4AF37]">{f.lead?.name || "Lead"}</div>
                <div className="text-xs text-slate-400">{f.lead?.mobile}</div>
              </Link>
              <div className="text-sm text-slate-300 mt-2 line-clamp-2">{f.notes || "No notes"}</div>
              <div className="mt-3 flex gap-2">
                {wa && <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className="text-emerald-400 hover:text-emerald-300"><WhatsappLogo size={18} weight="duotone" /></a>}
                {f.lead?.mobile && <a href={`tel:${f.lead.mobile}`} className="text-sky-400 hover:text-sky-300"><Phone size={18} weight="duotone" /></a>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
